import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthData } from "../api";

interface AuthState {
  // State
  token: string | null;
  asegurado: AuthData["asegurado"] | null;
  solicitudGuid: string | null;
  isAuthenticated: boolean;
  hasHydrated: boolean;

  // Actions
  setAuth: (authData: AuthData, solicitudGuid: string) => void;
  clearAuth: () => void;
  checkAuth: () => boolean;
  handleSessionExpired: () => void;
}

const getTokenFromZustandStorage = () => {
  try {
    const zustandStorage = localStorage.getItem("auth-storage");
    if (!zustandStorage) return null;

    const parsed = JSON.parse(zustandStorage);
    return parsed.state?.token || null;
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      asegurado: null,
      solicitudGuid: null,
      isAuthenticated: false,
      hasHydrated: false,

      // Set authentication data after successful login
      setAuth: (authData: AuthData, solicitudGuid: string) => {
        set({
          token: authData.accessToken,
          asegurado: authData.asegurado,
          solicitudGuid,
          isAuthenticated: true,
        });
      },

      // Clear authentication data on logout or session expiration
      clearAuth: () => {
        set({
          token: null,
          asegurado: null,
          solicitudGuid: null,
          isAuthenticated: false,
        });
      },

      // Check if user is authenticated
      checkAuth: () => {
        const state = get();

        const zustandStorage = localStorage.getItem("auth-storage");
        let storedToken = null;

        if (zustandStorage) {
          try {
            const parsedStorage = JSON.parse(zustandStorage);
            storedToken = parsedStorage.state?.token || null;
          } catch {
            // Si hay error parseando, limpiar todo
            localStorage.removeItem("auth-storage");
            set({
              isAuthenticated: false,
              token: null,
              asegurado: null,
              solicitudGuid: null,
            });
            return false;
          }
        }

        if (!zustandStorage && state.isAuthenticated) {
          set({
            isAuthenticated: false,
            token: null,
            asegurado: null,
            solicitudGuid: null,
          });
          return false;
        }

        if (!state.token && state.isAuthenticated) {
          set({
            isAuthenticated: false,
            token: null,
            asegurado: null,
            solicitudGuid: null,
          });
          return false;
        }

        const isAuth = !!(
          state.token &&
          state.asegurado &&
          state.solicitudGuid
        );

        try {
          const parts = storedToken.split(".");
          if (parts.length !== 3) {
            throw new Error("Formato JWT inválido");
          }

          const payload = JSON.parse(atob(parts[1]));
          const isExpired = payload.exp && payload.exp * 1000 < Date.now();

          if (isExpired) {
            throw new Error("Token expirado");
          }
        } catch (error) {
          console.log("Token inválido:");
          localStorage.removeItem("auth-storage");
          set({
            isAuthenticated: false,
            token: null,
            asegurado: null,
            solicitudGuid: null,
          });
          return false;
        }

        if (state.token !== storedToken || state.isAuthenticated !== true) {
          set({ isAuthenticated: isAuth, token: storedToken });
        }

        return isAuth;
      },

      // Handle session expiration
      handleSessionExpired: () => {
        get().clearAuth();
        if (typeof window !== "undefined") {
          window.location.href = "/sesion-expirada";
        }
      },

      setHasHydrated: (state: boolean) => {
        set({ hasHydrated: state });
      },
    }),
    {
      name: "auth-storage",
      // Migrate old localStorage data to Zustand
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hasHydrated = true;
        }

        if (state && typeof window !== "undefined") {
          // Check for old localStorage format and migrate
          const oldToken = localStorage.getItem("accessToken");
          const oldAsegurado = localStorage.getItem("asegurado");
          const oldSolicitudGuid = localStorage.getItem("solicitudGuid");

          if (oldToken && oldAsegurado && oldSolicitudGuid && !state.token) {
            try {
              const asegurado = JSON.parse(oldAsegurado);
              state.token = oldToken;
              state.asegurado = asegurado;
              state.solicitudGuid = oldSolicitudGuid;
              state.isAuthenticated = true;

              // Clean up old localStorage
              localStorage.removeItem("accessToken");
              localStorage.removeItem("asegurado");
              localStorage.removeItem("solicitudGuid");
            } catch (error) {
              console.error("[v0] Error migrating auth data:", error);
            }
          }
        }
      },
    }
  )
);
